import { mediaQueryLarge, isMobileBreakpoint } from '@theme/utilities';

// Accordion
// Still extends HTMLElement over Component so that refs are still available to parent components (e.g. SortingFilterComponent)
class AccordionCustom extends HTMLElement {
  /**
   * Remembers each accordion's user-set open/closed state for the lifetime of
   * the page. The facets form morphs its section on every filter change, which
   * re-runs connectedCallback and would otherwise reset each group to its
   * default-open state. Keyed by the inner <details> id (stable per group).
   * @type {Map<string, boolean>}
   */
  static #openState = new Map();

  /** @type {string | null} The key used to persist this accordion's state. */
  get #stateKey() {
    return this.querySelector('details')?.id || null;
  }

  /** Records the current open state so a later re-render can restore it. */
  #remember() {
    const key = this.#stateKey;
    if (key) AccordionCustom.#openState.set(key, this.details.open);
  }

  /** @type {HTMLDetailsElement} */
  get details() {
    const details = this.querySelector('details');

    if (!(details instanceof HTMLDetailsElement)) throw new Error('Details element not found');

    return details;
  }

  /** @type {HTMLElement} */
  get summary() {
    const summary = this.details.querySelector('summary');

    if (!(summary instanceof HTMLElement)) throw new Error('Summary element not found');

    return summary;
  }

  get #disableOnMobile() {
    return this.dataset.disableOnMobile === 'true';
  }

  get #disableOnDesktop() {
    return this.dataset.disableOnDesktop === 'true';
  }

  get #closeWithEscape() {
    return this.dataset.closeWithEscape === 'true';
  }

  #controller = new AbortController();

  connectedCallback() {
    const { signal } = this.#controller;

    this.#setDefaultOpenState();

    this.addEventListener('keydown', this.#handleKeyDown, { signal });
    this.summary.addEventListener('click', this.handleClick, { signal });
    mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange, { signal });
  }

  /**
   * Handles the disconnect event.
   */
  disconnectedCallback() {
    // Disconnect all the event listeners
    this.#controller.abort();
  }

  /**
   * Handles the click event.
   * @param {Event} event - The event.
   */
  handleClick = (event) => {
    const isMobile = isMobileBreakpoint();
    const isDesktop = !isMobile;

    // Stop default behaviour from the browser
    if ((isMobile && this.#disableOnMobile) || (isDesktop && this.#disableOnDesktop)) {
      event.preventDefault();
      return;
    }

    // The click toggles the <details> (its default action, after this handler),
    // so record the state it is about to become — synchronously, since a
    // requestAnimationFrame callback never fires in a hidden/background tab.
    const key = this.#stateKey;
    if (key) AccordionCustom.#openState.set(key, !this.details.open);
  };

  /**
   * Handles the media query change event. A breakpoint change resets to the
   * breakpoint default, so the remembered state is cleared first.
   */
  #handleMediaQueryChange = () => {
    const key = this.#stateKey;
    if (key) AccordionCustom.#openState.delete(key);
    this.#setDefaultOpenState();
  };

  /**
   * Restores the user's remembered open state if there is one; otherwise sets
   * the default based on the `open-by-default-on-mobile` / `-on-desktop` attributes.
   */
  #setDefaultOpenState() {
    const key = this.#stateKey;
    if (key && AccordionCustom.#openState.has(key)) {
      this.details.open = AccordionCustom.#openState.get(key) ?? false;
      return;
    }

    const isMobile = isMobileBreakpoint();

    this.details.open =
      (isMobile && this.hasAttribute('open-by-default-on-mobile')) ||
      (!isMobile && this.hasAttribute('open-by-default-on-desktop'));
  }

  /**
   * Handles keydown events for the accordion
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyDown(event) {
    // Close the accordion when used as a menu
    if (event.key === 'Escape' && this.#closeWithEscape) {
      event.preventDefault();

      this.details.open = false;
      this.#remember();
      this.summary.focus();
    }
  }
}

if (!customElements.get('accordion-custom')) {
  customElements.define('accordion-custom', AccordionCustom);
}
